import React from 'react';
import cx from 'classnames';

import Button from '../../atoms/Button';
import Text from '../../atoms/Text';
import Border from '../../atoms/Border';
import Icon from '../../atoms/Icon';
import TextContainer from '../../molecules/TextContainer';
import TextDropDown from '../../molecules/TextDropDown';
import TextField from '../../molecules/TextField';
import UserSummary from '../../molecules/UserSummary';

import styles from './ButtonModal.module.scss';
import usePresenter from './ButtonModal.presenter';
import getProps from './ButtonModal.props';
import { ButtonModalCombinedProps } from './types';


const ButtonModal: React.FC<ButtonModalCombinedProps> = (props) => {
  const {
    type,
    styleType,
    className,
    classes,
    title,
    button,
    border,
    text,
    actionButton,
    cancelButton,
    userSummary,
    caption,
    beforeLabel,
    textContainer,
    icon,
    textField,
    afterLabel,
    textDropDown,
  } = usePresenter(props);

  const variantName = `${type}${styleType}`;
  const internalProps = getProps(variantName);
  const currentStyle = styles[`buttonModal${variantName}`];


  let componentView;

  switch (variantName) {
    case 'DefaultDefault': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <div className={cx(styles.topContent, classes?.topContent)}>
            <div className={cx(styles.headingContent, classes?.headingContent)}>
              <div className={cx(styles.content, classes?.content)}>
                <Text
                  className={cx(styles.title, classes?.title)}
                  {...internalProps.title}
                  {...title}/>
                <Button
                  className={cx(styles.button, classes?.button)}
                  {...internalProps.button}
                  {...button}/>
              </div>
              <Border
                className={cx(styles.border, classes?.border)}
                {...internalProps.border}
                {...border}/>
            </div>
            <Text
              className={cx(styles.text, classes?.text)}
              {...internalProps.text}
              {...text}/>
          </div>
          <div className={cx(styles.buttonContent, classes?.buttonContent)}>
            <Button
              className={cx(styles.actionButton, classes?.actionButton)}
              {...internalProps.actionButton}
              {...actionButton}/>
            <Button
              className={cx(styles.cancelButton, classes?.cancelButton)}
              {...internalProps.cancelButton}
              {...cancelButton}/>
          </div>
        </div>
      );
      break;
    }
    case 'DeleteUserDestructive': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <div className={cx(styles.topContent, classes?.topContent)}>
            <div className={cx(styles.headingContent, classes?.headingContent)}>
              <div className={cx(styles.content, classes?.content)}>
                <Text
                  className={cx(styles.title, classes?.title)}
                  {...internalProps.title}
                  {...title}/>
                <Button
                  className={cx(styles.button, classes?.button)}
                  {...internalProps.button}
                  {...button}/>
              </div>
              <Border
                className={cx(styles.border, classes?.border)}
                {...internalProps.border}
                {...border}/>
            </div>
            <Text
              className={cx(styles.text, classes?.text)}
              {...internalProps.text}
              {...text}/>
          </div>
          <div className={cx(styles.userContent, classes?.userContent)}>
            <UserSummary
              className={cx(styles.userSummary, classes?.userSummary)}
              {...internalProps.userSummary}
              {...userSummary}/>
          </div>
          <div className={cx(styles.buttonContent, classes?.buttonContent)}>
            <Button
              className={cx(styles.actionButton, classes?.actionButton)}
              {...internalProps.actionButton}
              {...actionButton}/>
            <Button
              className={cx(styles.cancelButton, classes?.cancelButton)}
              {...internalProps.cancelButton}
              {...cancelButton}/>
          </div>
        </div>
      );
      break;
    }
    case 'DisableUserDestructive': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <div className={cx(styles.topContent, classes?.topContent)}>
            <div className={cx(styles.headingContent, classes?.headingContent)}>
              <div className={cx(styles.content, classes?.content)}>
                <Text
                  className={cx(styles.title, classes?.title)}
                  {...internalProps.title}
                  {...title}/>
                <Button
                  className={cx(styles.button, classes?.button)}
                  {...internalProps.button}
                  {...button}/>
              </div>
              <Border
                className={cx(styles.border, classes?.border)}
                {...internalProps.border}
                {...border}/>
            </div>
            <Text
              className={cx(styles.text, classes?.text)}
              {...internalProps.text}
              {...text}/>
          </div>
          <div className={cx(styles.bottomContent, classes?.bottomContent)}>
            <div className={cx(styles.userContent, classes?.userContent)}>
              <UserSummary
                className={cx(styles.userSummary, classes?.userSummary)}
                {...internalProps.userSummary}
                {...userSummary}/>
            </div>
            <Text
              className={cx(styles.caption, classes?.caption)}
              {...internalProps.caption}
              {...caption}/>
          </div>
          <div className={cx(styles.buttonContent, classes?.buttonContent)}>
            <Button
              className={cx(styles.actionButton, classes?.actionButton)}
              {...internalProps.actionButton}
              {...actionButton}/>
            <Button
              className={cx(styles.cancelButton, classes?.cancelButton)}
              {...internalProps.cancelButton}
              {...cancelButton}/>
          </div>
        </div>
      );
      break;
    }
    case 'EmailUpdateDefault': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <div className={cx(styles.topContent, classes?.topContent)}>
            <div className={cx(styles.headingContent, classes?.headingContent)}>
              <div className={cx(styles.content, classes?.content)}>
                <Text
                  className={cx(styles.title, classes?.title)}
                  {...internalProps.title}
                  {...title}/>
                <Button
                  className={cx(styles.button, classes?.button)}
                  {...internalProps.button}
                  {...button}/>
              </div>
              <Border
                className={cx(styles.border, classes?.border)}
                {...internalProps.border}
                {...border}/>
            </div>
            <Text
              className={cx(styles.text, classes?.text)}
              {...internalProps.text}
              {...text}/>
          </div>
          <div className={cx(styles.updateContent, classes?.updateContent)}>
            <Text
              className={cx(styles.beforeLabel, classes?.beforeLabel)}
              {...internalProps.beforeLabel}
              {...beforeLabel}/>
            <div className={cx(styles.actionContent, classes?.actionContent)}>
              <TextContainer
                className={cx(styles.textContainer, classes?.textContainer)}
                {...internalProps.textContainer}
                {...textContainer}/>
            </div>
            <Icon
              className={cx(styles.icon, classes?.icon)}
              {...internalProps.icon}
              {...icon}/>
            <TextField
              className={cx(styles.textField, classes?.textField)}
              {...internalProps.textField}
              {...textField}/>
          </div>
          <div className={cx(styles.buttonContent, classes?.buttonContent)}>
            <Button
              className={cx(styles.actionButton, classes?.actionButton)}
              {...internalProps.actionButton}
              {...actionButton}/>
            <Button
              className={cx(styles.cancelButton, classes?.cancelButton)}
              {...internalProps.cancelButton}
              {...cancelButton}/>
          </div>
        </div>
      );
      break;
    }
    case 'UpdateDefault': {
      componentView = (
        <div className={cx(currentStyle, className)}>
          <div className={cx(styles.topContent, classes?.topContent)}>
            <div className={cx(styles.headingContent, classes?.headingContent)}>
              <div className={cx(styles.content, classes?.content)}>
                <Text
                  className={cx(styles.title, classes?.title)}
                  {...internalProps.title}
                  {...title}/>
                <Button
                  className={cx(styles.button, classes?.button)}
                  {...internalProps.button}
                  {...button}/>
              </div>
              <Border
                className={cx(styles.border, classes?.border)}
                {...internalProps.border}
                {...border}/>
            </div>
            <Text
              className={cx(styles.text, classes?.text)}
              {...internalProps.text}
              {...text}/>
          </div>
          <div className={cx(styles.updateContent, classes?.updateContent)}>
            <div className={cx(styles.labelContent, classes?.labelContent)}>
              <Text
                className={cx(styles.beforeLabel, classes?.beforeLabel)}
                {...internalProps.beforeLabel}
                {...beforeLabel}/>
              <Text
                className={cx(styles.afterLabel, classes?.afterLabel)}
                {...internalProps.afterLabel}
                {...afterLabel}/>
            </div>
            <div className={cx(styles.actionContent, classes?.actionContent)}>
              <TextContainer
                className={cx(styles.textContainer, classes?.textContainer)}
                {...internalProps.textContainer}
                {...textContainer}/>
              <Icon
                className={cx(styles.icon, classes?.icon)}
                {...internalProps.icon}
                {...icon}/>
              <TextDropDown
                className={cx(styles.textDropDown, classes?.textDropDown)}
                {...internalProps.textDropDown}
                {...textDropDown}/>
            </div>
          </div>
          <div className={cx(styles.buttonContent, classes?.buttonContent)}>
            <Button
              className={cx(styles.actionButton, classes?.actionButton)}
              {...internalProps.actionButton}
              {...actionButton}/>
            <Button
              className={cx(styles.cancelButton, classes?.cancelButton)}
              {...internalProps.cancelButton}
              {...cancelButton}/>
          </div>
        </div>
      );
      break;
    }
    default:
      componentView = <div>�</div>;
      break;
  }

  return componentView;
};

export default ButtonModal;

